import React from 'react';
import { motion } from 'framer-motion';
import { Database, Shield, Lock, Eye, Key, FileCheck, Server, Cloud, CheckCircle, ArrowRight, Network, AlertTriangle } from 'lucide-react';

interface DataSecurityProps {
  setCurrentPage: (page: string) => void;
}

const DataSecurity: React.FC<DataSecurityProps> = ({ setCurrentPage }) => {
  const features = [
    {
      icon: Lock,
      title: "Data Encryption",
      description: "Advanced encryption solutions to protect sensitive data at rest and in transit."
    },
    {
      icon: Eye,
      title: "Access Control",
      description: "Granular access controls and authentication mechanisms to prevent unauthorized data access."
    },
    {
      icon: Key,
      title: "Key Management",
      description: "Secure key management solutions for cryptographic operations and data protection."
    },
    {
      icon: FileCheck,
      title: "Data Governance",
      description: "Comprehensive data governance frameworks to ensure compliance and security."
    }
  ];

  const services = [
    {
      title: "Data Protection",
      items: [
        "End-to-end encryption",
        "Secure data storage",
        "Data loss prevention",
        "Backup and recovery"
      ]
    },
    {
      title: "Access Management",
      items: [
        "Identity verification",
        "Role-based access control",
        "Multi-factor authentication",
        "Session management"
      ]
    },
    {
      title: "Compliance & Audit",
      items: [
        "Regulatory compliance",
        "Security audits",
        "Policy enforcement",
        "Activity monitoring"
      ]
    }
  ];

  const solutions = [
    {
      icon: Server,
      title: "Enterprise Data Security",
      description: "Comprehensive data security solutions for enterprise-scale organizations."
    },
    {
      icon: Cloud,
      title: "Cloud Data Protection",
      description: "Secure cloud storage and data protection for distributed environments."
    },
    {
      icon: Network,
      title: "Network Security",
      description: "Advanced network security measures to protect data in transit."
    }
  ];

  const dataProtection = [
    {
      icon: Shield,
      title: "Prevention",
      items: [
        "Data classification",
        "Access controls",
        "Encryption protocols",
        "Security policies"
      ]
    },
    {
      icon: Eye,
      title: "Detection",
      items: [
        "Anomaly detection",
        "Threat monitoring",
        "Data scanning",
        "Behavioral analysis"
      ]
    },
    {
      icon: AlertTriangle,
      title: "Response",
      items: [
        "Incident handling",
        "Breach notification",
        "Recovery procedures",
        "Post-incident analysis"
      ]
    }
  ];

  return (
    <div className="min-h-screen hero-gradient pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block mb-6"
          >
            <Database className="h-16 w-16 text-primary animate-glow" />
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-bold mb-6"
          >
            <span className="text-gradient">
              Data Security Services
            </span>
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-muted max-w-3xl mx-auto"
          >
              Comprehensive data protection solutions to secure your organization's most valuable assets
          </motion.p>
        </div>

        {/* Features Grid */}
        <div className="mb-20">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              <span className="text-gradient">Core Security Features</span>
            </h2>
            <p className="text-lg text-muted">
              Advanced data security features to protect your sensitive information
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + index * 0.1 }}
                className="glass-panel rounded-xl p-6 hover-card"
              >
                <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg mb-6">
                  <feature.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-3">
                  {feature.title}
                </h3>
                <p className="text-muted">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Data Protection Framework */}
        <div className="mb-20">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              <span className="text-gradient">Data Protection Framework</span>
            </h2>
            <p className="text-lg text-muted">
              Comprehensive approach to data security and protection
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {dataProtection.map((category, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + index * 0.1 }}
                className="glass-panel rounded-xl p-8 hover-card"
              >
                <div className="flex items-center mb-6">
                  <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg">
                    <category.icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-semibold ml-4">
                    {category.title}
                  </h3>
                </div>
                <ul className="space-y-4">
                  {category.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-primary mr-3" />
                      <span className="text-muted">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Services Section */}
        <div className="mb-20">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              <span className="text-gradient">Security Services</span>
            </h2>
            <p className="text-lg text-muted">
              Comprehensive data security services for your organization
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + index * 0.1 }}
                className="glass-panel rounded-xl p-8 hover-card"
              >
                <h3 className="text-2xl font-semibold mb-6">
                  {service.title}
                </h3>
                <ul className="space-y-4">
                  {service.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-primary mr-3" />
                      <span className="text-muted">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Solutions Section */}
        <section className="py-20 bg-gray-50 dark:bg-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                <span className="gradient-text">Enterprise Solutions</span>
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300">
                Scalable data security solutions for modern enterprises
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {solutions.map((solution, index) => (
                <div key={index} className="bg-white dark:bg-gray-900 rounded-xl p-8 text-center hover-card">
                  <div className="flex justify-center mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center">
                      <solution.icon className="h-8 w-8 text-white" />
                    </div>
                  </div>
                  <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                    {solution.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    {solution.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="text-center"
        >
          <div className="glass-panel rounded-2xl p-8 md:p-12">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-6">
                Protect Your Data Today
              </h2>
              <p className="text-lg text-muted mb-8 max-w-2xl mx-auto">
                Get started with our comprehensive data security solutions
              </p>
              <button 
                onClick={() => setCurrentPage('get-started')}
                className="btn-primary"
              >
                <span className="flex items-center justify-center">
                  Request Consultation
                  <ArrowRight className="ml-2 h-5 w-5" />
                </span>
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default DataSecurity;